import { t, useLanguage } from "@/lib/i18n";
import HeaderAccount from "@/components/HeaderAccount";
import { RELIGIONS } from "@/components/ReligionSelect";
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { spouseIds } from "@/lib/spouses";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import FamilyTreeCanvas from "@/components/FamilyTreeCanvas";
import MemberModal from "@/components/MemberModal";
import MemberDetailDrawer from "@/components/MemberDetailDrawer";
import AddRelativeDialog from "@/components/AddRelativeDialog";
import AccessControlModal from "@/components/AccessControlModal";
import { computeCollapseAllKeys, ancestorKeys, linkedMemberView, toggleAncestorLayer, largestTreeRoot } from "@/lib/tree";
import { displayMemberName } from "@/lib/memberName";
import { TreePine, Plus, Search, LogOut, Loader2, User, Menu, X, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { gotraTree } from "@/lib/gotraTree";

export default function TreeApp() {
  useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    const dismiss = (event) => {
      if (event.type === "keydown") {
        if (event.key !== "Escape") return;
        menuButtonRef.current?.focus();
      } else if (menuRef.current?.contains(event.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", dismiss);
    return () => { document.removeEventListener("pointerdown", dismiss); document.removeEventListener("keydown", dismiss); };
  }, [menuOpen]);
  const isGuest = user?.role === "guest";
  const [allPersons, setPersons] = useState([]);
  const [gotras, setGotras] = useState([]);
  const [castes, setCastes] = useState([]);
  const [activeReligion, setActiveReligion] = useState((isGuest && searchParams.get("religion")) || user?.religion || "Hindu");
  const [activeCaste, setActiveCaste] = useState((isGuest && searchParams.get("caste")) || user?.caste || "");
  const [activeGotra, setActiveGotra] = useState((isGuest && searchParams.get("gotra")) || user?.gotra || "");
  const persons = useMemo(() => gotraTree(allPersons, activeGotra, activeCaste, activeReligion),
    [allPersons, activeGotra, activeCaste, activeReligion]);
  const [loading, setLoading] = useState(true);
  const [selfProfile, setSelfProfile] = useState(false);
  const [linkedMemberId, setLinkedMemberId] = useState(null);
  const defaultRootId = useMemo(() => linkedMemberId ? null : largestTreeRoot(persons), [linkedMemberId, persons]);
  const [claiming, setClaiming] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const [addingRelative, setAddingRelative] = useState(null);
  const [selected, setSelected] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [query, setQuery] = useState("");
  const [treeFitRevision, setTreeFitRevision] = useState(0);
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const [collapsedAncestorKeys, setCollapsedAncestorKeys] = useState(() => new Set());
  const toggleAncestors = useCallback((key, id) => {
    setFocusRequest({ id, align: "center" });
    setCollapsedAncestorKeys((previous) => toggleAncestorLayer(persons, previous, key));
  }, [persons]);
  const [highlightId, setHighlightId] = useState(null);
  const [focusRequest, setFocusRequest] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const treeRef = useRef(null);
  const initialLinkedView = useRef(false);

  const toggleCollapse = useCallback((key, id) => {
    setFocusRequest({ id, align: "center" });
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const collapseAll = () => { setTreeFitRevision((revision) => revision + 1); setCollapsedAncestorKeys(new Set()); setCollapsedKeys(new Set(computeCollapseAllKeys(persons))); };
  const expandAll = () => { setTreeFitRevision((revision) => revision + 1); setCollapsedAncestorKeys(new Set()); setCollapsedKeys(new Set()); };

  const panToMember = (p) => {
    setCollapsedAncestorKeys(new Set());
    // auto-expand any collapsed ancestors so the target is visible
    const anc = new Set(ancestorKeys(persons, p.id));
    if (anc.size) {
      setCollapsedKeys((prev) => {
        if (![...prev].some((k) => anc.has(k))) return prev;
        return new Set([...prev].filter((k) => !anc.has(k)));
      });
    }
    setHighlightId(p.id);
    setShowResults(false);
    setQuery(p.full_name);
    setFocusRequest({ id: p.id });
  };

  const navigateToRelative = (person) => {
    const gotra = person.gotra || "";
    setActiveReligion(person.religion || "Hindu");
    setActiveCaste(person.caste || "");
    const targetTree = gotraTree(allPersons, gotra, person.caste || "", person.religion || "Hindu");
    const view = linkedMemberView(targetTree, person.id, Infinity);
    setActiveGotra(gotra);
    setCollapsedAncestorKeys(view.ancestors);
    setCollapsedKeys(view.descendants);
    setSelected(null);
    setShowResults(false);
    setQuery(person.full_name);
    setHighlightId(person.id);
    setFocusRequest({ id: person.id, align: "top" });
  };
  const onFocusHandled = useCallback((request) => {
    setFocusRequest((current) => current === request ? null : current);
  }, []);

  const load = useCallback(async () => {
    try {
      const [members, catalog, casteCatalog] = await Promise.all([api.get("/persons"), api.get("/gotras"), api.get("/castes")]);
      setPersons(members.data);
      setGotras(catalog.data);
      setCastes(casteCatalog.data);
    } catch (e) {
      toast.error(t("Failed to load family tree"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const changed = async (event) => {
      await load();
      try {
        const { data } = await api.get(`/persons/${event.detail}`);
        setSelected((current) => current?.id === event.detail ? data : current);
      } catch { /* A removed member no longer has a drawer to refresh. */ }
    };
    window.addEventListener("member-media-changed", changed);
    return () => window.removeEventListener("member-media-changed", changed);
  }, [load]);

  const changeGotra = (value) => {
    setCollapsedAncestorKeys(new Set());
    setActiveGotra(value);
    setSelected(null);
    setHighlightId(null);
    setFocusRequest(null);
    setQuery("");
    setShowResults(false);
    setCollapsedKeys(new Set());
  };

  const changeCaste = (value) => {
    setActiveCaste(value);
    changeGotra("");
  };

  const openAdd = () => { setSelfProfile(false); setInitialValues(null); setEditing(null); setModalOpen(true); };
  const openEdit = (p) => { setSelfProfile(false); setInitialValues(null); setSelected(null); setEditing(p); setModalOpen(true); };
  const openAddRelative = (person, relation) => {
    if (["child", "spouse"].includes(relation) && !["Male", "Female"].includes(person.gender)) {
      toast.error(t("Set this member's gender before adding a relative."));
      return;
    }
    setSelected(null);
    setAddingRelative({ person, relation });
  };
  const createRelative = (person, relation) => {
    setSelfProfile(false);
    setAddingRelative(null);
    const values = { religion: person.religion || "Hindu", caste: person.caste || "", gotra: person.gotra || "" };
    if (relation === "child") {
      if (!["Male", "Female"].includes(person.gender)) {
        toast.error(t("Set this member's gender before adding a relative."));
        return;
      }
      values[person.gender === "Female" ? "mother_id" : "father_id"] = person.id;
      const spouses = spouseIds(person);
      if (person.gender === "Male" && spouses.length === 1) {
        values.mother_id = spouses[0];
      }
    } else if (relation === "spouse") {
      values.gender = person.gender === "Male" ? "Female" : "Male";
      values.spouse_ids = [person.id];
    } else {
      values.gender = relation === "father" ? "Male" : "Female";
      values.children_ids = [person.id];
    }
    setInitialValues(values);
    setEditing(null);
    setSelected(null);
    setModalOpen(true);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/persons/${deleteTarget.id}`);
      toast.success(t("Member removed"));
      setDeleteTarget(null);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(t("Failed to delete"));
    }
  };

  useEffect(() => {
    if (!isGuest) api.get("/self-profile").then(({ data }) => setLinkedMemberId(data.member_id)).catch(() => {});
  }, [isGuest, allPersons]);
  useEffect(() => {
    if (initialLinkedView.current || !linkedMemberId) return;
    const member = allPersons.find((person) => person.id === linkedMemberId);
    if (!member) return;
    initialLinkedView.current = true;
    const gotra = member.gotra || "";
    setActiveReligion(member.religion || "Hindu");
    setActiveCaste(member.caste || "");
    const view = linkedMemberView(gotraTree(allPersons, gotra, member.caste || "", member.religion || "Hindu"), member.id);
    setActiveGotra(gotra);
    setCollapsedAncestorKeys(view.ancestors);
    setCollapsedKeys(view.descendants);
    setHighlightId(member.id);
    setFocusRequest({ id: member.id, align: "top" });
  }, [linkedMemberId, allPersons]);

  const createMyProfile = () => {
    setSelfProfile(true); setEditing(null);
    const [first_name, ...rest] = (user?.name || "").trim().split(/\s+/);
    setInitialValues({ first_name, last_name: rest.join(" "), email: user?.email || "" });
    setModalOpen(true);
  };
  const claimMember = async () => {
    setClaiming(true);
    try {
      const { data } = await api.post(`/persons/${selected.id}/claim`);
      if (data.status === "Approved") {
        setLinkedMemberId(data.member_id);
        toast.success(t("Profile linked successfully."));
      } else {
        toast.success(t("Profile link requested. Track admin approval in My Requests."));
      }
      setSelected(null);
    } catch (error) { toast.error(t(error.response?.data?.detail || t("Could not request profile link"))); }
    finally { setClaiming(false); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const matches = query.trim()
    ? persons.filter((p) => p.full_name.toLowerCase().includes(query.toLowerCase()) || (p.gotra || "").toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : [];


  return (
    <div className="tree-app flex flex-col bg-[#F4EFEB]">
      {/* top bar */}
      <header className="tree-header bg-[#2C1E16] text-amber-50 px-3 sm:px-6 flex flex-wrap items-center gap-2 shrink-0 z-30 shadow-md">
        <div className="flex-1 min-w-0 sm:max-w-sm sm:mx-4 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 z-10" />
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 150)}
            placeholder={t("Search family members…")}
            aria-label={t("Search family members")}
            className="pl-9 bg-[#3a2a1f] border-[#5a4433] text-amber-50 placeholder:text-stone-400 h-10"
            data-testid="member-search-input"
          />
          {showResults && matches.length > 0 && (
            <div className="absolute top-12 left-0 right-0 bg-white rounded-xl border border-[#E6DEC3] shadow-xl overflow-hidden z-40 max-h-80 overflow-y-auto" data-testid="search-results">
              {matches.map((p) => (
                <button
                  key={p.id}
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => panToMember(p)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#F4EFEB] text-left transition-colors border-b border-[#F4EFEB] last:border-0"
                  data-testid={`search-result-${p.id}`}
                >
                  <div className="w-8 h-8 shrink-0 rounded-lg bg-[#F4EFEB] border border-[#E6DEC3] flex items-center justify-center text-stone-400">
                    <User size={15} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{displayMemberName(p)}</p>
                    {p.gotra && <p className="text-[11px] text-amber-800/70 font-mono truncate">{p.gotra}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-3 sm:ml-auto">
          <div className="max-w-[120px] sm:max-w-[220px] text-right leading-tight" data-testid="tree-signed-in-user">
            <p className="text-sm font-medium truncate" title={user?.name || t("Guest")}>{user?.name || t("Guest")}</p>
            {user?.email && <p className="text-xs text-amber-100/70 truncate" title={user.email}>{user.email}</p>}
          </div>
          <HeaderAccount className="text-amber-50 hover:bg-white/10" />
          <div ref={menuRef} className="relative" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setMenuOpen(false); }}>
            <Button ref={menuButtonRef} variant="ghost" aria-label={t("Account and administration menu")} aria-expanded={menuOpen} aria-controls="tree-account-menu" onClick={() => setMenuOpen((open) => !open)} className="px-3">
              {menuOpen ? <X size={20} /> : <Menu size={20} />}<span className="hidden sm:inline ml-2">{t("Menu")}</span>
            </Button>
            <nav id="tree-account-menu" aria-label={t("Account and administration")} className={`tree-account-menu ${menuOpen ? "flex" : "hidden"}`} onClick={(event) => { if (event.target.closest("button")) setMenuOpen(false); }}>
          <p className="px-3 py-2 text-sm break-words text-amber-100/70">{user?.name || t("Guest")}</p>
          {user?.role === "admin" && <Button variant="ghost" onClick={() => navigate("/admin")}>{t("Admin")}</Button>}
          {!isGuest && <Button variant="ghost" onClick={() => navigate("/account")}>{t("Account settings")}</Button>}
          {!isGuest && <Button variant="ghost" onClick={() => navigate("/albums")}>{t("My Albums")}</Button>}
          <Button variant="ghost" onClick={() => navigate("/about")}>{t("About VanshVriksham")}</Button>
          {!isGuest && <Button variant="ghost" onClick={() => navigate("/submissions")}>{t("My submissions")}</Button>}
          {!isGuest && <Button variant="ghost" onClick={() => navigate("/subscriptions")}>{t("Advertisements, Plans & Subscriptions")}</Button>}
          {!isGuest && <Button variant="ghost" onClick={() => navigate("/change-password")}>{t("Change password")}</Button>}
          {!isGuest && <Button variant="ghost" onClick={() => { if (!linkedMemberId) { createMyProfile(); return; } const member = allPersons.find((p) => p.id === linkedMemberId); if (member) { navigateToRelative(member); setSelected(member); } else toast.error(t("Your linked member is unavailable.")); }}>{linkedMemberId ? t("My Profile") : t("Create My Profile")}</Button>}
          {!isGuest && <Button variant="ghost" onClick={() => navigate(`/tree-builder?${new URLSearchParams({ religion: activeReligion, caste: activeCaste, gotra: activeGotra })}`)}>{t("Build my family")}</Button>}
          {!isGuest && <Button variant="ghost" onClick={() => navigate("/requests")}>{t("My Requests")}</Button>}
          {!isGuest && <Button variant="ghost" onClick={() => navigate("/tree-builder?community_only=1")}>{t("Request caste or gotra")}</Button>}
          <Button variant="ghost" onClick={doLogout} data-testid="logout-button"><LogOut size={18} className="mr-2" />{isGuest ? t("Exit guest mode") : t("Logout")}</Button>
            </nav>
          </div>
          {!isGuest && <Button onClick={openAdd} aria-label={t("Add member")} className="gold-btn text-[#2C1E16] font-semibold border-0 px-3" data-testid="add-member-button">
            <Plus size={16} className="sm:mr-1" /> <span className="hidden sm:inline">{t("Add Member")}</span>
          </Button>}
          {isGuest && <span className="hidden sm:inline text-xs text-amber-100">{t("Guest (read only)")}</span>}
        </div>
      </header>

      <div className="sm:hidden bg-white border-b px-3 py-2 flex items-center gap-3 shrink-0">
        <Button variant="outline" aria-expanded={filtersOpen} aria-controls="tree-filters" onClick={() => setFiltersOpen((open) => !open)}><SlidersHorizontal size={16} className="mr-2" />{filtersOpen ? t("Hide filters") : t("Choose tree")}</Button>
        <p className="text-xs text-stone-600 min-w-0 truncate">{activeGotra || activeCaste || activeReligion} · {persons.length}{t(" members")}</p>
      </div>
      <div id="tree-filters" className={`tree-filters bg-white border-b px-4 py-3 ${filtersOpen ? "grid" : "hidden"} sm:flex sm:flex-wrap items-center gap-3`}>
        <label htmlFor="tree-religion" className="text-sm font-medium">{t("Religion")}</label>
        <select id="tree-religion" data-testid="tree-religion" value={activeReligion}
          onChange={(e) => { setActiveReligion(e.target.value); changeCaste(""); }} className="border rounded-md p-2 bg-white max-w-xs">
          {RELIGIONS.map((religion) => <option key={religion} value={religion}>{t(religion)}</option>)}
        </select>
        <label htmlFor="tree-caste" className="text-sm font-medium">{t("Caste")}</label>
        <select id="tree-caste" data-testid="tree-caste" value={activeCaste} onChange={(e) => changeCaste(e.target.value)} className="border rounded-md p-2 bg-white max-w-xs">
          <option value="">{t("Select Caste")}</option>
          {castes.filter((c) => c.religion === activeReligion).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
        <label htmlFor="tree-gotra" className="text-sm font-medium">{t("Gotra tree")}</label>
        <select id="tree-gotra" data-testid="tree-gotra" value={activeGotra} disabled={!activeCaste} onChange={(e) => changeGotra(e.target.value)} className="border rounded-md p-2 bg-white max-w-xs">
          <option value="">{activeCaste ? t("Unspecified gotra") : t("Select Caste first")}</option>
          {gotras.filter((g) => g.caste === activeCaste && g.religion === activeReligion).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
        <p className="text-xs text-stone-500">{t("Community members and their direct children · ")}{persons.length}{t(" members")}</p>
      </div>

      {/* canvas */}
      <div className="flex-1 min-h-0 relative tree-grid overflow-hidden" data-testid="tree-canvas-area">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center text-stone-400 font-serif text-xl">{t("Loading tree…")}</div>
        ) : persons.length === 0 ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <div className="w-20 h-20 rounded-2xl bg-white border border-[#E6DEC3] flex items-center justify-center mb-6 shadow-sm">
            </div>
            <h2 className="font-serif text-3xl font-bold text-stone-900">{t("Your tree is empty")}</h2>
            <p className="text-stone-600 mt-2 max-w-md">{isGuest ? t("Choose another Gotra tree to browse public members.") : t("Start by adding your first family member.")}</p>
            <div className="flex flex-wrap gap-3 mt-7 justify-center">
              {!isGuest && <Button onClick={openAdd} className="gold-btn text-[#2C1E16] font-semibold border-0 h-11 px-6" data-testid="empty-add-button">
                <Plus size={18} className="mr-1" />{t(" Add First Member")}</Button>}
              {!isGuest && <Button variant="outline" onClick={() => navigate("/tree-builder")}>{t("Build my family")}</Button>}

            </div>
          </div>
        ) : (
          <>
            <FamilyTreeCanvas
              key={JSON.stringify([activeReligion, activeCaste, activeGotra])}
              ref={treeRef}
              fitRevision={treeFitRevision}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
              persons={persons}
              defaultRootId={defaultRootId}
              selectedId={selected?.id || highlightId}
              focusRequest={focusRequest}
              onFocusHandled={onFocusHandled}
              onSelect={(p) => { setHighlightId(null); setSelected(p); }}
              collapsedKeys={collapsedKeys}
              collapsedAncestorKeys={collapsedAncestorKeys}
              onToggleAncestors={toggleAncestors}
              onToggleCollapse={toggleCollapse}
            />
          </>
        )}
      </div>

      <MemberModal selfProfile={selfProfile} open={!isGuest && modalOpen} onClose={() => setModalOpen(false)} persons={allPersons} editing={editing} onSaved={load} gotras={gotras} castes={castes} defaultReligion={activeReligion} defaultCaste={activeCaste} defaultGotra={activeGotra} initialValues={initialValues} />
      <MemberDetailDrawer
        person={selected}
        selectionActions={!isGuest && !linkedMemberId && selected ? <div className="mb-4 space-y-2"><Button disabled={claiming} onClick={claimMember} data-testid="claim-member-button">{claiming ? t("Requesting...") : user?.role === "admin" ? t("This is me - link profile") : t("This is me - request profile link")}</Button><p className="text-xs text-stone-500">{user?.role === "admin" ? t("Your profile will be linked immediately.") : t("Requires admin approval. After approval, you can edit your profile and submit changes for review.")}</p></div> : null}
        persons={allPersons}
        onClose={() => setSelected(null)}
        onEdit={openEdit}
        onDelete={(p) => setDeleteTarget(p)}
        onShare={(p) => setShareTarget(p)}
        onNavigateMember={navigateToRelative}
        onAddRelative={openAddRelative}
      />
      <AccessControlModal open={!!shareTarget} onClose={() => setShareTarget(null)} person={shareTarget} />
      {addingRelative && <AddRelativeDialog {...addingRelative} persons={allPersons}
        onClose={() => setAddingRelative(null)} onCreate={createRelative} onSaved={load} />}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">{t("Remove ")}{displayMemberName(deleteTarget)}?</AlertDialogTitle>
            <AlertDialogDescription>{t("This permanently removes the member and detaches all their family links. This cannot be undone.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-confirm-button">{t("Remove")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
